import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/language-context';
import { fetchBlogPostBySlug, fetchBlogPosts, BlogPost } from '@/lib/api';
import { Calendar, User, Share2 } from 'lucide-react';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useLanguage();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return;
      
      setLoading(true);
      const postData = await fetchBlogPostBySlug(slug, 'KYC');
      setPost(postData);
      
      if (postData) {
        const allPosts = await fetchBlogPosts('KYC');
        const related = allPosts
          .filter((p) => p.category === postData.category && p.slug !== slug)
          .slice(0, 3);
        setRelatedPosts(related);
      }
      
      setLoading(false);
    };
    
    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">
            {t('blog.notFound')}
          </h1>
          <Link to="/blog" className="text-blue-600 hover:underline">
            {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  const content = language === 'en' ? post.content : post.contentId;
  const title = language === 'en' ? post.title : post.titleId;

  return (
    <div className="py-20">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-sm text-gray-600">
          <Link to="/" className="hover:text-blue-600">
            {t('nav.home')}
          </Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-blue-600">
            {t('blog.title')}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">{title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm">
                  {post.category}
                </span>
                <span className="text-gray-500 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.publishDate || post.date || '').toLocaleDateString(language === 'en' ? 'en-US' : 'zh-TW', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>

              <div className="flex items-center gap-4 text-gray-600">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
              </div>
            </div>

            <div className="prose prose-lg max-w-none mb-12">
              {content.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-3xl font-bold mt-10 mb-6">
                      {line.replace('## ', '')}
                    </h2>
                  );
                }
                if (line.trim() === '') {
                  return <br key={index} />;
                }
                return (
                  <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                    {line}
                  </p>
                );
              })}
            </div>

            {post.tags.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-bold mb-4">
                  {t('blog.tags')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-blue-600 hover:text-white transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-b py-6 mb-12">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                {t('blog.share')}
              </h3>
              <div className="flex gap-3">
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Facebook
                </button>
                <button className="bg-blue-400 text-white px-6 py-2 rounded-lg hover:bg-blue-500 transition-colors">
                  Twitter
                </button>
                <button className="bg-blue-700 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors">
                  LinkedIn
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            {relatedPosts.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold mb-6">
                  {t('blog.relatedArticles')}
                </h3>
                <div className="space-y-6">
                  {relatedPosts.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/blog/${related.slug}`}
                      className="block group"
                    >
                      <h4 className="font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                        {language === 'en' ? related.title : related.titleId}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {language === 'en' ? related.excerpt : related.excerptId}
                      </p>
                      <span className="text-sm text-gray-500">
                        {new Date(related.publishDate || related.date || '').toLocaleDateString(language === 'en' ? 'en-US' : 'zh-TW')}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-blue-600 text-white rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">
                {t('blog.needHelp')}
              </h3>
              <p className="mb-6">
                {t('blog.needHelpDesc')}
              </p>
              <Link
                to="/contact"
                className="block w-full bg-white text-blue-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                {t('contact.title')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
